import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * DEV-268 — link e token do formulário de alinhamento do programa
 * (`POST /programs/:id/alignment-access`, DEV-258). A rota é IDEMPOTENTE: cria
 * o acesso uma vez e devolve sempre o mesmo `path`/`formId` e, com a cifra
 * configurada no Core, o mesmo `token` (AC-12). Por isso ela vive numa query
 * (cache por programa, `staleTime: Infinity`, D11): reabrir o diálogo não
 * dispara request nem pode "gerar" valor novo.
 *
 * O token NUNCA entra em log, analytics ou URL — só no estado desta query.
 */
export interface AlignmentAccess {
  created: boolean;
  /** Linha anterior sem token recuperável: token trocado uma vez (D2). */
  rotated?: boolean;
  formId: string;
  /** `/descricao-vaga/{tenant}/{formId}` — o SPA prefixa a origem (D1). */
  path: string;
  /** `null` só quando o Core não tem o segredo da cifra e o acesso já existia. */
  token: string | null;
}

export class AlignmentAccessFetchError extends Error {
  readonly name = 'AlignmentAccessFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export function alignmentAccessQueryKey(programId: string) {
  return ['recruiter', 'programs', 'alignment-access', programId] as const;
}

export function alignmentAccessQueryOptions(programId: string) {
  return queryOptions({
    queryKey: alignmentAccessQueryKey(programId),
    queryFn: async ({ signal }) => {
      const res = await fetchClient<AlignmentAccess>({
        method: 'POST',
        url: `/programs/${programId}/alignment-access`,
        signal,
      });
      if (res.error) {
        throw new AlignmentAccessFetchError(
          `Failed to load alignment access (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (failureCount, err) => {
      if (err instanceof AlignmentAccessFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
